import { computed, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';

import { useCityInfo } from '@/entities/city/api/useCityInfo';

const RUSSIA_SLUG = 'russia';

const getRouteParam = (param: string | string[] | undefined): string | undefined => {
  if (Array.isArray(param)) {
    return param[0];
  }

  return param;
};

/**
 * Определяет, относится ли текущая туровая локация к России.
 *
 * Для стран и регионов использует параметры маршрута, для городов — страну из city info.
 * Явная страна в маршруте имеет приоритет над citySlug из маршрутов отправления.
 *
 * @returns флаг российской локации
 */
export const useIsRussianTourLocation = (): { isRussianLocation: ComputedRef<boolean> } => {
  const route = useRoute();
  const citySlug = computed(() => getRouteParam(route.params.citySlug) ?? '');
  const country = computed(() => getRouteParam(route.params.country));
  const countrySlug = computed(() => getRouteParam(route.params.countrySlug));
  const hasExplicitCountry = computed(() => !!(country.value || countrySlug.value));
  const isCityInfoEnabled = computed(() => !!citySlug.value && !hasExplicitCountry.value);

  const { cityInfo } = useCityInfo(citySlug, isCityInfoEnabled);

  const isRussianLocation = computed(() => {
    if (hasExplicitCountry.value) {
      return country.value === RUSSIA_SLUG || countrySlug.value === RUSSIA_SLUG;
    }

    return cityInfo.value?.country?.slug === RUSSIA_SLUG;
  });

  return {
    isRussianLocation,
  };
};
